import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { PINNED_LOOKUP_RELAYS } from '@/lib/appRelays';
import { findCachedArchiveEvent } from '@/hooks/useArchiveTagCounts';
import { findPinnedFallback } from '@/hooks/usePinnedNotes';
import { findRecoveredNote } from '@/lib/recoveredNotes';

/**
 * Open one record by id, looking in the same places the feed does so that
 * anything the feed shows can also be opened:
 *
 *   1. the normal relays (quick),
 *   2. the wide set of deep-history relays the feed's pinned lookup uses,
 *   3. the signed copies built into the site for records relays no longer serve,
 *   4. the archive's saved copy in this browser,
 *   5. the copies built into the site for the curated records.
 *
 * A real signed copy from a relay always wins over a built-in reconstruction.
 */
export function useNote(id: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<NostrEvent | null>({
    queryKey: ['note', id],
    queryFn: async ({ signal }) => {
      if (!id) return null;

      const within = (ms: number) =>
        signal ? AbortSignal.any([signal, AbortSignal.timeout(ms)]) : AbortSignal.timeout(ms);

      try {
        const [event] = await nostr.query([{ kinds: [1], ids: [id], limit: 1 }], {
          signal: within(6000),
        });
        if (event) return event;
      } catch {
        /* try the wider net */
      }

      try {
        const [event] = await nostr
          .group(PINNED_LOOKUP_RELAYS)
          .query([{ kinds: [1], ids: [id], limit: 1 }], { signal: within(15000) });
        if (event) return event;
      } catch {
        /* fall back to what we already hold */
      }

      return findRecoveredNote(id) ?? findCachedArchiveEvent(id) ?? findPinnedFallback(id);
    },
    enabled: Boolean(id),
    // A record that was found is kept for five minutes. One that wasn't is looked
    // for again next time, so a slow relay can't leave it "not found" for long.
    staleTime: (query) => (query.state.data ? 5 * 60 * 1000 : 0),
  });
}
